import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, filter, pairwise, startWith, takeUntil } from 'rxjs';
import { selectNraIndicator, selectRegistrationType, selectStateCode } from '../../../account-info-header/reducers';
import { OptionDropdown, SelectControl } from '../../../create-request/models/OptionDropdownSelect';
import { DistributionReason } from '../../../create-request/models/internal-transfer/DistributionReason';
import { createControl } from '../../../create-request/utilities/formUtilities';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { Constants, RothConversion, RothWarnings, distributionReasons, fedTaxErrorMap, pdfLabelsSubmitDetails, recipientType, registrationTypes, stateCodes, stateTaxErrorMap, transactionDetailLabels } from '../../../shared/utilities/Constants';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { percentFormatter } from '../../../shared/utilities/PercentFormatter';
import { percentAllowedChars } from '../../../shared/utilities/regex-patterns';

import { selectCheckDistributionReason, selectCheckDistributionReasonsList, selectCheckFedTaxPercent, selectCheckShouldShowStateTax, selectCheckStateCode, selectCheckStateTaxPercent, selectCheckStateTypeIndicator, selectPayeeType, selectShouldDisableFieldForEditQcd } from '../../reducers';
import { selectCheckInternationalFedTaxError } from '../../../create-request/reducers';
import { FeatureFlagService } from '../../../shared/services/feature-flag-service';

@Component({
  standalone: false,
  selector: 'mm-sa-check-tax-withholdings',
  templateUrl: './sa-check-tax-withholdings.component.html',
  styleUrls: ['./sa-check-tax-withholdings.component.scss']
})
export class SaCheckTaxWithholdingsComponent implements OnInit {
  @Input() parentForm: UntypedFormGroup;
  @Output() showCashieringIRAAgreementModal = new EventEmitter<boolean>();
  @Input() shouldDisableFieldForEditQcd: boolean = false;
  taxWithholdingText: string = Constants.taxWithholdingText;
  nraText: string = Constants.nraText;
  nraIndicator: boolean;
  readonly fedTaxControl: string = 'fedTaxPercent';
  readonly stateTaxControl: string = 'stateTaxPercent';
  readonly distributionReasonControl: string = 'distributionReason';
  percentAllowedChars = percentAllowedChars;
  defaultFedTax: string;
  defaultStateTax: string;
  destroy$: Subject<boolean> = new Subject<boolean>();
  stateTypeIndicator: string;
  stateCode: string;
  qcdDistributionInformation: string = Constants.qcdDistributionInformation;
  regType: string;
  shouldShowStateTax: boolean;
  hasOnlyStateTaxError: boolean = false;
  distributionReasonsList: DistributionReason[];
  distributionReasonModel: SelectControl = {
    title: 'Distribution reason',
    controlName: 'distributionReason',
    options: [],
    validationMessage: 'Distribution reason needs to be selected',
  };
  distributionReasonTooltip: string = Constants.itCheckDistributionReasonTooltip;
  distributionReasonTooltipHtml: string = Constants.qcdDistributionReasonTooltip;
  distributionReason: string;
  qualifiedDistReason: string = pdfLabelsSubmitDetails.qualifiedDistReason;
  qcdDistReason: string = RothConversion.DistributionReasons.QCD_DESCRIPTION;
  qualifiedDistReasonWarning: string = RothWarnings.QualifiedDistributionWarning;
  disableStateTax: boolean;
  saStateTaxLabel: string = transactionDetailLabels.stateTax;
  hasInternationalFedTaxError: boolean = false;
  internationalFedTaxErrorText = fedTaxErrorMap.internationalFedTaxError;

  private store: Store = inject(Store);
  public validationService: ValidateInputService = inject(ValidateInputService);
  private featureFlagService: FeatureFlagService = inject(FeatureFlagService);

  ngOnInit(): void {
    this.store.select(selectCheckFedTaxPercent).pipe(takeUntil(this.destroy$)).subscribe(fedTax => this.defaultFedTax = fedTax);
    this.store.select(selectCheckStateTaxPercent).pipe(takeUntil(this.destroy$)).subscribe(stateTax => this.defaultStateTax = stateTax);

    createControl(this.parentForm, this.fedTaxControl, percentFormatter(this.defaultFedTax), [Validators.required, CustomValidators.zeroOrGreater, CustomValidators.wholeNumber, Validators.maxLength(10), CustomValidators.greaterThan99FedRequestCheck]);
    createControl(this.parentForm, this.stateTaxControl, percentFormatter(this.defaultStateTax), [Validators.required, CustomValidators.zeroOrGreater, Validators.maxLength(10), CustomValidators.greaterThan99State]);
    createControl(this.parentForm, this.distributionReasonControl, '', [Validators.required]);

    this.parentForm.setValidators(CustomValidators.sumDoesNotExceed99Point99('fedTaxPercent', 'stateTaxPercent'));

    this.store.select(selectNraIndicator).pipe(takeUntil(this.destroy$)).subscribe(_nraIndicator => this.nraIndicator = _nraIndicator ?? false);
    this.store.select(selectCheckStateTypeIndicator).pipe(takeUntil(this.destroy$)).subscribe(_stateTypeIndicator => {
      this.stateTypeIndicator = _stateTypeIndicator;
      if (this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateTypeIndicator?.toUpperCase() === stateCodes.Mississippi) {
        this.parentForm.addValidators(
          CustomValidators.requiresStateTaxForEarlyDistribution
        );
      }
    });
    this.store.select(selectCheckStateCode).pipe(takeUntil(this.destroy$)).subscribe(_stateCode => this.stateCode = _stateCode);
    this.store.select(selectRegistrationType).pipe(takeUntil(this.destroy$)).subscribe(_regType => this.regType = _regType);
    this.store.select(selectCheckShouldShowStateTax).pipe(takeUntil(this.destroy$)).subscribe(_shouldShowStateTax => this.shouldShowStateTax = _shouldShowStateTax);
    this.store.select(selectCheckDistributionReasonsList).pipe(takeUntil(this.destroy$)).subscribe(_distributionReasonsList => this.distributionReasonsList = _distributionReasonsList);
    this.store.select(selectStateCode).pipe(takeUntil(this.destroy$)).subscribe(stateCode => {
      if (stateCode && !this.nraIndicator) {
        this.saStateTaxLabel = `${transactionDetailLabels.stateTax} (${stateCode})`;
      }
    });
    this.store.select(selectShouldDisableFieldForEditQcd).pipe(takeUntil(this.destroy$)).subscribe(isEditToQcdFLow => {
      if (isEditToQcdFLow) {
        this.parentForm.controls['distributionReason'].addValidators([CustomValidators.MustSelectQCDToProceed]);
      } else {
        this.parentForm.controls['distributionReason'].removeValidators([CustomValidators.MustSelectQCDToProceed]);
      }
    });

    this.store.select(selectCheckInternationalFedTaxError)
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasError) => {
        if (hasError) {
          this.fedTaxPercent?.setErrors({ 'internationalFedTaxError': true });
          this.hasInternationalFedTaxError = true;
        } else {
          this.hasInternationalFedTaxError = false;
        }
      });

    this.store.select(selectPayeeType)
      .pipe(
        takeUntil(this.destroy$),
        startWith(''),
        pairwise(),
      ).subscribe(([previousPayeeType, currentPayeeType]) => {
        let mappedReasons = this.distributionReasonsList;

        if (currentPayeeType === recipientType.firstParty) {
          mappedReasons = [];
          this.distributionReasonsList.forEach((ele) => {
            if (ele.description !== distributionReasons.qcd && ele.description.trim() !== distributionReasons.qcdY4) {
              mappedReasons.push(ele);
            }
          })
        }
        if (currentPayeeType === recipientType.thirdParty && this.regType !== 'Inh IRA' && this.regType !== 'Inh IRA QP') {
          mappedReasons = [];
          this.distributionReasonsList.forEach((ele) => {
            if (ele.description !== distributionReasons.qcdY4) {
              mappedReasons.push(ele);
            }
          })
        }

        if (previousPayeeType !== currentPayeeType && previousPayeeType !== '') { //Reset distributionReason selection when payeeType is changed
          this.parentForm?.controls.distributionReason.setValue('');
        }

        this.distributionReasonModel.options = mappedReasons.map((element) =>
          new OptionDropdown(element.description, element.description));
      });

    this.store
      .select(selectCheckDistributionReason)
      .pipe(
        takeUntil(this.destroy$),
        startWith(''),
        pairwise(),
        filter(([drPrevious, drLatest]) => drLatest !== drPrevious)
      )
      .subscribe(([drPrevious, drLatest]) => {
        this.distributionReason = drLatest;
        if (drLatest === this.qualifiedDistReason || drLatest === this.qcdDistReason || drLatest === distributionReasons.qcdY4) {
          this.resetAndDisableTaxFields();
        }
        if (drPrevious === this.qualifiedDistReason || drPrevious === this.qcdDistReason || drPrevious === distributionReasons.qcdY4) {
          this.enableTaxFields();
        }
      });

    if (this.isEducationalSavings() || this.shouldDisableFieldForEditQcd) {
      this.fedTaxPercent.disable();
      this.stateTaxPercent.disable();
    }

    this.disableStateTaxFormControl();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get fedTaxPercent() {
    return this.parentForm?.controls.fedTaxPercent;
  }

  get stateTaxPercent() {
    return this.parentForm?.controls.stateTaxPercent;
  }

  get isFedTaxInvalid(): boolean {
    return ((this.fedTaxPercent?.touched && this.fedTaxPercent?.invalid) || this.fedAndStateTaxSumError !== '');
  }

  get isStateTaxInvalid(): boolean {
    return ((this.stateTaxPercent?.touched && this.stateTaxPercent?.invalid) || this.fedAndStateTaxSumError !== '');
  }

  get selectedDistributionReason() {
    return this.parentForm?.controls.distributionReason;
  }

  get isQcdDistributionReason() {
    return this.selectedDistributionReason.value === distributionReasons.qcd;
  }

  get isQcdY4DistributionReason() {
    return this.selectedDistributionReason.value === distributionReasons.qcdY4;
  }

  onFedTaxBlur(): void {
    if (this.fedTaxPercent.valid) {
      this.fedTaxPercent?.setValue(percentFormatter(this.fedTaxPercent.value));
    }
  }

  onStateTaxBlur(): void {
    if (this.stateTaxPercent.valid) {
      this.stateTaxPercent?.setValue(percentFormatter(this.stateTaxPercent.value));
    }
  }

  get fedTaxError() {
    if (this.fedTaxPercent && this.fedTaxPercent.errors) {
      const errorKeys: string[] = Object.keys(this.fedTaxPercent.errors ?? {});
      if (errorKeys.length > 0) {
        const filteredErrorKeys = this.hasInternationalFedTaxError
          ? errorKeys.filter(key => key !== 'internationalFedTaxError')
          : errorKeys;

        if (filteredErrorKeys.length > 0) {
          return fedTaxErrorMap[filteredErrorKeys[0] as keyof typeof fedTaxErrorMap];
        }
      }
    }
  }

  get stateTaxError() {
    if (this.stateTaxPercent && this.stateTaxPercent.errors || this.hasMississippiStateTaxErrorText) {
      var stateTaxErrorMS = this.featureFlagService.featureFlags.enableSpecialStateTax && this.parentForm?.errors?.stateTaxErrorTextMS ? { 'stateTaxErrorTextMS': true } : null;
      const errorKeys: string[] = Object.keys(
        (stateTaxErrorMS ? stateTaxErrorMS : this.stateTaxPercent.errors) ?? {}
      );
      if (errorKeys.length > 0) {
        return stateTaxErrorMap[errorKeys[0] as keyof typeof stateTaxErrorMap];
      }
    }
  }

  get stateTaxAttentionRequiredWarningMessage() {
    if (this.stateTypeIndicator?.toUpperCase() === stateCodes.Massachusetts) {
      return Constants.retirementStateTaxAttentionRequiredTextMA;
    } else if (this.stateTypeIndicator?.toUpperCase() === stateCodes.Mississippi) {
      return Constants.retirementStateTaxAttentionRequiredTextMS;
    } else {
      return '';
    }
  }

  get showMassachusettsDistributionWarning(): boolean {
    return this.featureFlagService?.featureFlags?.enableSpecialStateTax && this.stateTypeIndicator?.toUpperCase() === stateCodes.Massachusetts;
  }

  get showMississippiEarlyDistributionWarning(): boolean {
    return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateTypeIndicator?.toUpperCase() === stateCodes.Mississippi && this.selectedDistributionReason?.value?.toUpperCase() === distributionReasons.early;
  }

  get hasMississippiStateTaxErrorText(): boolean {
    return this.featureFlagService.featureFlags.enableSpecialStateTax && this.stateTypeIndicator?.toUpperCase() === stateCodes.Mississippi && this.parentForm?.errors?.stateTaxErrorTextMS;
  }

  get fedAndStateTaxSumError(): string {
    if (this.parentForm && this.parentForm.errors) {
      const errorKeys: string[] = Object.keys(
        this.parentForm.errors ?? {}
      );
      this.hasOnlyStateTaxError = this.featureFlagService.featureFlags.enableSpecialStateTax ? errorKeys.length === 1 && errorKeys.includes('stateTaxErrorTextMS') : false;
      if (errorKeys.length > 0 && !this.hasOnlyStateTaxError) {
        return 'Total tax withholding cannot exceed 99.99%. Update values or request a one-time check to withhold 100%';
      }
    }
    return '';
  }

  disableStateTaxFormControl() {
    if (!(this.shouldShowStateTax || this.isEducationalSavings()) || this.nraIndicator) {
      this.stateTaxPercent.disable();
      this.disableStateTax = true;
    }
  }

  isEducationalSavings(): boolean {
    return this.regType === registrationTypes.educationalSavings;
  }

  displayIraAgreementModal() {
    this.showCashieringIRAAgreementModal.emit(true);
  }

  resetAndDisableTaxFields(): void {
    this.fedTaxPercent?.setValue(Constants.zeroPercent);
    this.fedTaxPercent?.disable();
    this.stateTaxPercent?.setValue(Constants.zeroPercent);
    this.stateTaxPercent?.disable();
  }

  enableTaxFields(): void {
    this.fedTaxPercent?.setValue(percentFormatter(this.defaultFedTax));
    this.stateTaxPercent?.setValue(percentFormatter(this.defaultStateTax));
    if (this.shouldDisableFieldForEditQcd) {
      return;
    }
    this.fedTaxPercent?.enable();
    if (!this.disableStateTax) {
      this.stateTaxPercent?.enable();
    }
  }
}
